// Code snippet block — tabbed view of curl + JavaScript invocations for the
// currently selected endpoint, with a copy-to-clipboard button. Snippets are
// derived from the aspirational `https://api.tagboard.io/v1/...` URL so they
// document the target shape rather than today's Supabase-direct call path.

import React, {useState} from 'react'
import clsx from 'clsx'
import {Copy} from 'lucide-react'
import {showToast} from '../../components/common/Toast'
import {buildDisplayUrl, type EndpointDef} from './endpoints'

type Lang = 'curl' | 'javascript'

interface CodeSnippetProps {
  endpoint: EndpointDef
  params: Record<string, string>
}

export const CodeSnippet: React.FC<CodeSnippetProps> = ({endpoint, params}) => {
  const [lang, setLang] = useState<Lang>('curl')
  const url = buildDisplayUrl(endpoint, params)
  const snippet = lang === 'curl' ? curlSnippet(url) : jsSnippet(url)

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(snippet)
      showToast('Copied to clipboard.', 'success')
    } catch {
      showToast('Copy failed — select the text and copy manually.', 'error')
    }
  }

  return (
    <div className="flex flex-col gap-sm">
      <div className="flex items-center justify-between">
        <div className="flex gap-xs">
          <LangTab active={lang === 'curl'} onClick={() => setLang('curl')}>
            curl
          </LangTab>
          <LangTab active={lang === 'javascript'} onClick={() => setLang('javascript')}>
            JavaScript
          </LangTab>
        </div>
        <button
          type="button"
          onClick={handleCopy}
          className="flex items-center gap-xs rounded-md border border-surfaceBorder px-md py-xs text-xs text-textSecondary hover:border-primary hover:text-primary"
          aria-label="Copy snippet"
        >
          <Copy size={12} />
          Copy
        </button>
      </div>

      <pre className="overflow-x-auto rounded-md bg-surfaceLight p-md font-mono text-xs leading-relaxed text-textPrimary">
        {snippet}
      </pre>

      <div className="text-xs italic text-textMuted">
        Snippet shows the v1 production URL. In internal preview the playground calls Supabase
        directly — migrate before sharing externally.
      </div>
    </div>
  )
}

const LangTab: React.FC<{
  active: boolean
  onClick: () => void
  children: React.ReactNode
}> = ({active, onClick, children}) => (
  <button
    type="button"
    onClick={onClick}
    className={clsx(
      'rounded-md px-md py-xs text-xs transition-colors',
      active
        ? 'bg-primaryBg font-semibold text-primary'
        : 'text-textSecondary hover:text-textPrimary',
    )}
  >
    {children}
  </button>
)

function curlSnippet(url: string): string {
  return [
    `curl -X GET "${url}" \\`,
    `  -H "Authorization: Bearer YOUR_API_KEY" \\`,
    `  -H "Accept: application/json"`,
  ].join('\n')
}

function jsSnippet(url: string): string {
  return [
    `const res = await fetch("${url}", {`,
    `  headers: {`,
    `    Authorization: \`Bearer \${process.env.TAGBOARD_API_KEY}\`,`,
    `    Accept: "application/json",`,
    `  },`,
    `})`,
    `if (!res.ok) throw new Error(\`HTTP \${res.status}\`)`,
    `const { data, meta } = await res.json()`,
    `console.log(meta.count, data)`,
  ].join('\n')
}
